import React, { useRef, useState, useEffect } from 'react';
import { Undo, Redo, Eraser, PenTool, Trash2 } from 'lucide-react';

export default function DigitalWritingBoard({ 
  onSave, 
  initialData, 
  questionId,
  pageNumber,
  readOnly = false 
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  const [isDrawing, setIsDrawing] = useState(false);
  const [mode, setMode] = useState('pen'); // 'pen' or 'eraser'
  const [history, setHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(-1);
  const [lastSaved, setLastSaved] = useState(null);

  // Initialize canvas and handle resize
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    const ctx = canvas.getContext('2d');
    
    const resizeCanvas = () => {
      // Save current content before resize
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      const tCtx = tempCanvas.getContext('2d');
      tCtx.drawImage(canvas, 0, 0);
      
      // Resize
      canvas.width = container.offsetWidth;
      canvas.height = container.offsetHeight;
      
      // Restore content
      ctx.drawImage(tempCanvas, 0, 0);
      
      // Set default styles
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Set white background if empty
      if (historyStep === -1) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        saveState(); // Initial state
      }
    };

    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();

    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  // Load initial data if provided
  useEffect(() => {
    if (initialData) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        saveState(false);
      };
      img.src = initialData;
    }
  }, [initialData]);

  // Auto-save hook
  useEffect(() => {
    if (readOnly) return;
    
    const autoSaveInterval = setInterval(() => {
      if (historyStep > 0 && canvasRef.current) {
        const currentData = canvasRef.current.toDataURL('image/png');
        if (currentData !== lastSaved) {
          onSave(questionId, pageNumber, currentData);
          setLastSaved(currentData);
        }
      }
    }, 10000); // Save every 10 seconds

    return () => clearInterval(autoSaveInterval);
  }, [historyStep, lastSaved, questionId, pageNumber, onSave, readOnly]);

  const saveState = (clearForward = true) => {
    if (readOnly) return;
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL();
    
    if (clearForward) {
      const newHistory = history.slice(0, historyStep + 1);
      newHistory.push(dataUrl);
      setHistory(newHistory);
      setHistoryStep(newHistory.length - 1);
    } else {
      setHistory(prev => [...prev, dataUrl]);
      setHistoryStep(prev => prev + 1);
    }
  };

  const undo = () => {
    if (readOnly || historyStep <= 0) return;
    
    const newStep = historyStep - 1;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
    };
    img.src = history[newStep];
    setHistoryStep(newStep);
  };

  const redo = () => {
    if (readOnly || historyStep >= history.length - 1) return;
    
    const newStep = historyStep + 1;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
    };
    img.src = history[newStep];
    setHistoryStep(newStep);
  };

  const clearCanvas = () => {
    if (readOnly) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    saveState();
  };

  const startDrawing = (e) => {
    if (readOnly) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || readOnly) return;
    e.preventDefault(); // Prevent scrolling on touch
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    
    ctx.lineTo(x, y);
    
    if (mode === 'pen') {
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.globalCompositeOperation = 'source-over';
    } else if (mode === 'eraser') {
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 20;
      ctx.globalCompositeOperation = 'source-over';
    }
    
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing && !readOnly) {
      setIsDrawing(false);
      saveState();
      
      // Trigger instant save on pen up if there was a change
      if (onSave) {
        const currentData = canvasRef.current.toDataURL('image/png');
        onSave(questionId, pageNumber, currentData);
        setLastSaved(currentData);
      }
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {!readOnly && (
        <div className="flex items-center justify-between p-3 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setMode('pen')}
              className={`p-2 rounded-lg transition ${mode === 'pen' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600 hover:bg-gray-200'}`}
              title="Pen Tool"
            >
              <PenTool size={20} />
            </button>
            <button 
              onClick={() => setMode('eraser')}
              className={`p-2 rounded-lg transition ${mode === 'eraser' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600 hover:bg-gray-200'}`}
              title="Eraser"
            >
              <Eraser size={20} />
            </button>
            <div className="w-px h-6 bg-gray-300 mx-2"></div>
            <button 
              onClick={undo}
              disabled={historyStep <= 0}
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Undo"
            >
              <Undo size={20} />
            </button>
            <button 
              onClick={redo}
              disabled={historyStep >= history.length - 1}
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Redo"
            >
              <Redo size={20} />
            </button>
          </div>
          <button 
            onClick={clearCanvas}
            className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition flex items-center gap-2"
            title="Clear Board"
          >
            <Trash2 size={20} /> <span className="text-sm font-medium hidden sm:inline">Clear</span>
          </button>
        </div>
      )}
      
      <div 
        ref={containerRef} 
        className="flex-1 w-full relative bg-gray-100"
        style={{ cursor: mode === 'pen' ? 'crosshair' : 'cell', touchAction: 'none' }}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          className="absolute inset-0 w-full h-full"
        />
      </div>
      <div className="p-2 text-xs text-center text-gray-400 bg-gray-50 border-t">
        {readOnly ? 'Read Only Mode' : 'Board Auto-Saves every 10 seconds. Use Stylus, Mouse, or Touch.'}
      </div>
    </div>
  );
}
